import {
  IPaymentDetailInfo,
  IPaymentInfo,
  IPaymentInfoType,
} from "@/domain/entities/payment";
import TYPES from "@/ioc/identifiers";
import { SERVICE_UID } from "@/types";
import useGetAccessToken from "@/use/useGetAccessToken";
import useGetProtocolHost from "@/use/useGetProtocolHost";
import { inject, injectable } from "inversify";
import { combine, Result } from "neverthrow";
import { HttpError } from "../shared/errors/httpError";
import { ParseError } from "../shared/errors/parseError";
import { IHttpService } from "../shared/httpService";
import {
  IPaymentDetailInfoParser,
  HttpPaymentDetailInfoResponse,
  IPaymentInfoParser,
  IPaymentInfoResponse,
  IPaymentRepository,
  HttpPaymentMethodsResponse,
  IPaymentMethodParser,
} from "./paymentRepository.types";
import { IPaymentMethod } from "@/domain/entities/payment/paymentMethod.types";

@injectable()
export class PaymentRepository implements IPaymentRepository {
  @inject(TYPES.IPaymentInfoParser)
  private paymentInfoParser!: IPaymentInfoParser;

  @inject(TYPES.IPaymentDetailInfoParser)
  private paymentDetailInfoParser!: IPaymentDetailInfoParser;

  @inject(TYPES.IPaymentMethodParser)
  private paymentMethodParser!: IPaymentMethodParser;

  private httpService: IHttpService;

  constructor(@inject(TYPES.IHttpService) httpService: IHttpService) {
    this.httpService = httpService;
    this.httpService.initService(
      `${useGetProtocolHost()}/aibos/client/api/${
        process.env.VUE_APP_API_VERSION
      }/service/${SERVICE_UID}`
    );
  }

  async getPaymentInfos(
    dateFrom: string,
    dateTo: string,
    paymentType?: IPaymentInfoType
  ): Promise<Result<IPaymentInfo[], ParseError | HttpError>> {
    const parseTo = (response: IPaymentInfoResponse) => {
      const paymentInfos = response.data.map(this.paymentInfoParser.toDomain);
      return combine(paymentInfos);
    };
    let payTypeCodes = "PYT-PAY,PYT-CANCEL";
    if (paymentType) {
      payTypeCodes =
        paymentType === IPaymentInfoType.PAID ? "PYT-PAY" : "PYT-CANCEL";
    }

    return await this.httpService.get<IPaymentInfoResponse, IPaymentInfo[]>(
      {
        url: "/pay",
        config: {
          headers: {
            Authorization: `Bearer ${useGetAccessToken()}`,
          },
          params: {
            payTypeCodes: payTypeCodes,
            dateFrom,
            dateTo,
            sort: "endTs desc",
          },
        },
      },
      { parseTo }
    );
  }

  async getPaymentDetailInfo(
    payUid: string
  ): Promise<Result<IPaymentDetailInfo, ParseError | HttpError>> {
    const parseTo = (response: HttpPaymentDetailInfoResponse) => {
      return this.paymentDetailInfoParser.toDomain(response.data);
    };

    return await this.httpService.get<
      HttpPaymentDetailInfoResponse,
      IPaymentDetailInfo
    >(
      {
        url: `/pay/${payUid}`,
        config: {
          headers: {
            Authorization: `Bearer ${useGetAccessToken()}`,
          },
        },
      },
      { parseTo }
    );
  }

  async getPaymentMethods(
    turnId?: string
  ): Promise<Result<IPaymentMethod[], ParseError | HttpError>> {
    const parseTo = (response: HttpPaymentMethodsResponse) => {
      const paymentMethods = response.data.map(
        this.paymentMethodParser.toDomain
      );
      return combine(paymentMethods);
    };

    let url = "/book/pg";
    url += turnId ? `?lineTurnUid=${turnId}` : "";

    return await this.httpService.get<
      HttpPaymentMethodsResponse,
      IPaymentMethod[]
    >(
      {
        url,
        config: {
          headers: {
            Authorization: `Bearer ${useGetAccessToken()}`,
          },
        },
      },
      { parseTo }
    );
  }
}
